import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, shareReplay, take, throwError } from 'rxjs';
import { CatalogProvider, DataSourceMeta } from '@ams/dashboard';
import { DASHBOARD_SOURCE_ROUTES } from './dashboard-source-routes';

/**
 * Membora's implementation of the dashboard library's CatalogProvider contract.
 * The library never names this endpoint — the app owns the URL/transport, which
 * is what keeps the library backend-agnostic.
 */
@Injectable({ providedIn: 'root' })
export class DashboardCatalogService implements CatalogProvider {
  private readonly http = inject(HttpClient);
  private readonly url = '/api/dashboards/catalog';

  /** Memoized catalog fetch; cleared on failure so the next caller retries. */
  private sources$?: Observable<DataSourceMeta[]>;

  /**
   * The catalog is a static registry for the session, and every query widget on a
   * dashboard asks for it to type its columns — so fetch it once and replay. Without
   * this, a dashboard with N query widgets fires N identical requests on load.
   *
   * Only successes are cached: shareReplay would otherwise hand the same error to every
   * later subscriber for the rest of the session, so one flaky response would leave the
   * builder and every widget stuck until a page reload.
   */
  getSources(): Observable<DataSourceMeta[]> {
    this.sources$ ??= this.http.get<DataSourceMeta[]>(this.url).pipe(
      take(1),
      // Attach app-specific click-through routes per source (used to prefill
      // widget route config). Sources with no mapping keep no routes.
      map((sources) =>
        sources.map((s) => {
          const routes = DASHBOARD_SOURCE_ROUTES[s.id];
          return routes ? { ...s, listRoute: routes.list, detailRoute: routes.detail } : s;
        }),
      ),
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((err) => {
        this.sources$ = undefined;
        return throwError(() => err);
      }),
    );
    return this.sources$;
  }

  getSource(id: string): Observable<DataSourceMeta | null> {
    return this.getSources().pipe(
      map((sources) => sources.find((s) => s.id === id) ?? null),
    );
  }
}
